import { useFlags } from 'launchdarkly-react-client-sdk';

import { useFetchRecertDueDates } from '../GeneralAffordable/RecertAndHouseholdInfoCard/hooks/useFetchRecertDueDates';
import { HUD_PROGRAM_NAME } from '../GeneralAffordable/constants';

// Single source of truth for this flag key — LaunchDarkly flag keys have
// varied in casing/format across environments historically, hence checking
// both variants. Exported as a plain function so ResidentProfile (a class
// component, can't call hooks) and useEditHouseholdMemberDatesEnabled (for
// functional components) share the same check instead of duplicating it.
export const isEditHouseholdMemberDatesFlagEnabled = (flags) =>
  Boolean(
    flags?.editHouseholdMemberDates || flags?.['edit-household-member-dates'],
  );

// Same key-casing-safe check for the household-mi-mo-date-changes flag
// (LaunchDarkly may expose it camelCased or in its original kebab-case).
export const isHouseholdMiMoDateChangesFlagEnabled = (flags) =>
  Boolean(
    flags?.householdMiMoDateChanges || flags?.['household-mi-mo-date-changes'],
  );

export const useEditHouseholdMemberDatesEnabled = () => {
  const flags = useFlags();
  return isEditHouseholdMemberDatesFlagEnabled(flags);
};

export const useHudRecertDueDate = ({
  organizationId,
  propertyId,
  householdId,
  isHUDByUnit,
  isSection236 = false,
  floorPlanAffordablePrograms,
  selectedProperty,
}: {
  organizationId: ?string,
  propertyId: ?string,
  householdId: ?string,
  isHUDByUnit: boolean,
  isSection236?: boolean,
  floorPlanAffordablePrograms: Array<Object>,
  selectedProperty: Object,
}): {
  hudRecertDueDate: ?Object,
  isLoading: boolean,
  isError: boolean,
  refetch: Function,
} => {
  // Section 236 units have no assistance to terminate, so the HUD tab hides the TM and the
  // move-out rule cannot ask for one.
  const enabled = Boolean(
    isHUDByUnit && !isSection236 && organizationId && propertyId && householdId,
  );
  const { data, isLoading, isError, refetch } = useFetchRecertDueDates({
    organizationId,
    propertyId,
    householdId,
    floorPlanAffordablePrograms,
    selectedProperty,
    enabled,
  });

  return {
    hudRecertDueDate: enabled
      ? data?.[HUD_PROGRAM_NAME]?.recertDueDate ?? null
      : null,
    isLoading: enabled && isLoading,
    // A failed refetch keeps the cached data, so only a failed first load blocks.
    isError: enabled && isError && data == null,
    refetch,
  };
};
